'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { motion, type Variants } from 'motion/react';
import { getVariants, useAnimateIconContext, IconWrapper, type IconProps } from '@/components/animate-ui/icons/icon';
type ChartBarIncreasingProps = IconProps<keyof typeof animations>;
const animations = {
  default: (() => {
    const animation: Record<string, Variants> = {
      path4: {}
    };
    for (let i = 1; i <= 3; i++) {
      animation[`path${i}`] = {
        initial: {
          opacity: 1
        },
        animate: {
          opacity: [0, 1],
          pathLength: [0, 1],
          transition: {
            ease: 'easeInOut',
            duration: 0.4,
            delay: (i - 1) * 0.3
          }
        }
      };
    }
    return animation as Record<string, Variants>;
  })() satisfies Record<string, Variants>,
  'default-loop': (() => {
    const n = 3;
    const delayStep = 0.3;
    const segDuration = 0.4;
    const startOut = (i: number) => (n - i) * delayStep;
    const endOut = (i: number) => startOut(i) + segDuration;
    const outTotal = Math.max(...Array.from({
      length: n
    }, (_, k) => endOut(k + 1)));
    const startIn = (i: number) => outTotal + (i - 1) * delayStep;
    const endIn = (i: number) => startIn(i) + segDuration;
    const totalDuration = Math.max(...Array.from({
      length: n
    }, (_, k) => endIn(k + 1)));
    const animation: Record<string, Variants> = {};
    for (let i = 1; i <= n; i++) {
      const tSO = startOut(i) / totalDuration;
      const tEO = endOut(i) / totalDuration;
      const tSI = startIn(i) / totalDuration;
      const tEI = endIn(i) / totalDuration;
      animation[`path${i}`] = {
        initial: {
          opacity: 1,
          pathLength: 1
        },
        animate: {
          pathLength: [1, 1, 0, 0, 1],
          opacity: [1, 1, 0, 0, 1],
          transition: {
            ease: 'easeInOut',
            duration: totalDuration,
            times: [0, tSO, tEO, tSI, tEI]
          }
        }
      };
    }
    return animation as Record<string, Variants>;
  })() satisfies Record<string, Variants>,
  decreasing: {
    path1: {
      initial: {
        d: 'M7 6h3'
      },
      animate: {
        d: 'M7 6h12',
        transition: {
          duration: 0.5,
          ease: 'easeInOut'
        }
      }
    },
    path2: {
      initial: {
        d: 'M7 11h8'
      },
      animate: {
        d: 'M7 11h8',
        transition: {
          duration: 0.5,
          ease: 'easeInOut'
        }
      }
    },
    path3: {
      initial: {
        d: 'M7 16h12'
      },
      animate: {
        d: 'M7 16h3',
        transition: {
          duration: 0.5,
          ease: 'easeInOut'
        }
      }
    }
  } satisfies Record<string, Variants>
} as const;
function IconComponent(t0) {
  const $ = _c(16);
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = size;
  } else {
    props = $[1];
    size = $[2];
  }
  const {
    controls
  } = useAnimateIconContext();
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getVariants(animations);
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const variants = t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[4] !== controls) {
    t2 = <motion.path d="M7 6h3" variants={variants.path1} initial="initial" animate={controls} />;
    t3 = <motion.path d="M7 11h8" variants={variants.path2} initial="initial" animate={controls} />;
    t4 = <motion.path d="M7 16h12" variants={variants.path3} initial="initial" animate={controls} />;
    t5 = <motion.path d="M3 3v16a2 2 0 0 0 2 2h16" variants={variants.path4} initial="initial" animate={controls} />;
    $[4] = controls;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== props || $[10] !== size || $[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <motion.svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>{t2}{t3}{t4}{t5}</motion.svg>;
    $[9] = props;
    $[10] = size;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function ChartBarIncreasing(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <IconWrapper icon={IconComponent} {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export { animations, ChartBarIncreasing, ChartBarIncreasing as ChartBarIncreasingIcon, type ChartBarIncreasingProps, type ChartBarIncreasingProps as ChartBarIncreasingIconProps };